import { useEffect } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import type { HostBridge } from '../../../../host.js';
import type { Palette } from '../../../../_theme/palette.js';
import { backdropAnim, modalAnim } from '../../../../motion/keyframes.js';

interface ModalShellProps {
  palette: Palette;
  onDismiss: () => void;
  children: ReactNode;
  wide?: boolean;
  bridge?: HostBridge;
}

export function ModalShell({ palette, onDismiss, children, wide, bridge }: ModalShellProps): JSX.Element {
  // Reset the iframe to fullscreen when expanding from a banner strip.
  useEffect(() => {
    if (!bridge) return;
    bridge.send('chirp:surface:placement', { layout: 'modal' });
  }, [bridge]);

  return (
    <div role="dialog" aria-modal="true" onClick={onDismiss} style={backdropStyle(palette)}>
      <div onClick={(event) => event.stopPropagation()} style={panelStyle(palette, wide)}>
        {children}
      </div>
    </div>
  );
}

function backdropStyle(palette: Palette): CSSProperties {
  return {
    position: 'fixed',
    inset: 0,
    // Light dim only — backdrop-filter blur can't reach host content from
    // inside a cross-origin iframe (browser privacy restriction). The 0.3
    // opacity scrim keeps the host page visible (light, dark, branded — all
    // show through) while giving the modal panel readable contrast.
    background: 'rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: palette.font,
    animation: backdropAnim(),
  };
}

function panelStyle(palette: Palette, wide?: boolean): CSSProperties {
  return {
    width: '100%',
    // Scale with viewport but with a sensible floor (so mobile feels right)
    // and a ceiling (so ultra-wide monitors don't stretch the modal so wide
    // that line length hurts readability).
    maxWidth: wide ? 'clamp(720px, 80vw, 1200px)' : 'clamp(600px, 70vw, 960px)',
    maxHeight: 'calc(100vh - 48px)',
    background: palette.background,
    borderRadius: 20,
    boxShadow:
      '0 0 0 1px rgba(15, 23, 42, 0.05), 0 24px 48px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.08)',
    overflow: 'hidden',
    color: palette.text,
    display: 'flex',
    flexDirection: 'column',
    animation: modalAnim(),
    fontFamily: palette.font,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}
